import { FieldProps, FormikProps } from 'formik'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Form } from 'react-bulma-components'
import FormField, { FormFieldProps } from './form-field'
import { get } from 'lodash'
import {
  loadPlaceAutocompleteElement,
  mapGooglePlaceToAddress,
} from '../../../utils/google-maps-places'

export interface AddressAutocompleteFieldProps extends FormFieldProps, FieldProps {
  /** ISO2 country code used to filter suggestions. */
  countryCode?: string
  /** ISO 639-1 language for suggestion labels. */
  language?: string
}

/**
 * Apply Bulma-like sizing to the official Google Places autocomplete host element.
 */
const styleAutocompleteHost = (element: google.maps.places.PlaceAutocompleteElement): void => {
  element.style.width = '100%'
  element.style.display = 'block'
  element.style.borderRadius = '4px'
  element.style.border = '1px solid #dbdbdb'
  element.style.boxSizing = 'border-box'
  element.style.minHeight = '2.5em'
  element.style.colorScheme = 'light'
}

/**
 * Delivery address field using Google's official PlaceAutocompleteElement.
 * Falls back to a plain text input when the Maps API cannot be loaded (e.g. E2E without key).
 */
const AddressAutocompleteField: React.FunctionComponent<AddressAutocompleteFieldProps> = ({
  field,
  form,
  label,
  help,
  countryCode,
  language,
}) => {
  const hostRef = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null)
  const formRef = useRef<FormikProps<Record<string, unknown>>>(form)
  const selectHandlerRef = useRef<((event: Event) => void) | null>(null)
  const inputHandlerRef = useRef<(() => void) | null>(null)
  const isApplyingSelectionRef = useRef(false)
  // E2E forces plain-text fallback even when frontend/.env has a Maps API key.
  const [useFallbackInput, setUseFallbackInput] = useState(
    () => process.env.E2E_ADDRESS_FALLBACK === '1' || !process.env.GOOGLE_MAPS_API_KEY,
  )
  const [isLoadingPlace, setIsLoadingPlace] = useState(false)

  formRef.current = form

  const error = get(form.errors, field.name) as React.ReactNode

  /**
   * Push address-related values into Formik and trigger validation.
   * `address` is the full line shown in the widget; `streetAddress` is street-only for the API.
   */
  const syncAddressFields = useCallback(
    (updates: {
      address: string
      streetAddress?: string
      zip?: string
      city?: string
      addressComplete?: boolean
      streetNumber?: string
    }) => {
      const current = formRef.current.values
      formRef.current.setValues(
        {
          ...current,
          address: updates.address,
          streetAddress: updates.streetAddress ?? '',
          zip: updates.zip ?? '',
          city: updates.city ?? '',
          addressComplete: updates.addressComplete ?? false,
          streetNumber: updates.streetNumber ?? '',
        },
        true,
      )
      formRef.current.setFieldTouched('address', true, false)
      formRef.current.setFieldTouched('zip', true, false)
      formRef.current.setFieldTouched('city', true, false)
    },
    [],
  )

  /**
   * Mount Google's official autocomplete widget into the host container.
   */
  useEffect(() => {
    if (useFallbackInput || !hostRef.current) {
      return
    }

    let cancelled = false

    const mountWidget = async () => {
      try {
        const PlaceAutocompleteElement = await loadPlaceAutocompleteElement(language)
        if (cancelled || !hostRef.current) {
          return
        }

        const previousWidget = widgetRef.current
        if (previousWidget) {
          if (selectHandlerRef.current) {
            previousWidget.removeEventListener('gmp-select', selectHandlerRef.current)
          }
          if (inputHandlerRef.current) {
            previousWidget.removeEventListener('input', inputHandlerRef.current)
          }
          previousWidget.remove()
          widgetRef.current = null
        }

        const widget = new PlaceAutocompleteElement({
          includedRegionCodes: countryCode ? [countryCode.toLowerCase()] : undefined,
        })
        widget.setAttribute('data-testid', 'address-autocomplete')
        styleAutocompleteHost(widget)

        if (field.value) {
          widget.value = String(field.value)
        }

        const handleSelect = async (event: Event) => {
          const selectEvent = event as google.maps.places.PlacePredictionSelectEvent
          const placePrediction = selectEvent.placePrediction
          if (!placePrediction) {
            return
          }

          setIsLoadingPlace(true)
          isApplyingSelectionRef.current = true

          try {
            const place = placePrediction.toPlace()
            await place.fetchFields({
              fields: ['addressComponents', 'formattedAddress'],
            })

            const mapped = mapGooglePlaceToAddress(place)
            if (mapped) {
              const displayAddress =
                getPlacePredictionDisplayText(placePrediction) ||
                place.formattedAddress?.trim() ||
                mapped.formatted
              widget.value = displayAddress
              syncAddressFields({
                address: displayAddress,
                streetAddress: mapped.streetLine,
                zip: mapped.zip,
                city: mapped.city,
                addressComplete: mapped.isComplete,
                streetNumber: mapped.streetNumber,
              })
              return
            }

            const fallbackAddress = place.formattedAddress ?? widget.value
            widget.value = fallbackAddress
            syncAddressFields({
              address: fallbackAddress,
              streetAddress: fallbackAddress.split(',')[0]?.trim() ?? fallbackAddress,
              zip: '',
              city: '',
              addressComplete: false,
            })
          } catch {
            syncAddressFields({
              address: widget.value,
              streetAddress: '',
              zip: '',
              city: '',
              addressComplete: false,
            })
          } finally {
            setIsLoadingPlace(false)
            window.setTimeout(() => {
              isApplyingSelectionRef.current = false
            }, 0)
          }
        }

        const handleInput = () => {
          if (isApplyingSelectionRef.current) {
            return
          }
          syncAddressFields({
            address: widget.value,
            streetAddress: '',
            zip: '',
            city: '',
            addressComplete: false,
          })
        }

        selectHandlerRef.current = handleSelect
        inputHandlerRef.current = handleInput
        widget.addEventListener('gmp-select', handleSelect)
        widget.addEventListener('input', handleInput)

        hostRef.current.replaceChildren(widget)
        widgetRef.current = widget
      } catch {
        if (!cancelled) {
          setUseFallbackInput(true)
        }
      }
    }

    void mountWidget()

    return () => {
      cancelled = true
      const widget = widgetRef.current
      if (widget) {
        if (selectHandlerRef.current) {
          widget.removeEventListener('gmp-select', selectHandlerRef.current)
        }
        if (inputHandlerRef.current) {
          widget.removeEventListener('input', inputHandlerRef.current)
        }
        widget.remove()
        widgetRef.current = null
      }
    }
  }, [countryCode, language, useFallbackInput, syncAddressFields])

  /**
   * Fallback plain input for environments without a frontend Maps API key (E2E).
   * Accepts a full manual line `street, zip city` so zip/city hidden fields are populated.
   */
  const handleFallbackChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseManualFallbackAddress(event.target.value)
    field.onChange(event)
    syncAddressFields({
      address: parsed.address,
      streetAddress: parsed.streetAddress,
      zip: parsed.zip,
      city: parsed.city,
      addressComplete: parsed.isComplete,
    })
  }

  return (
    <FormField label={label} help={help} error={error}>
      {/* Keeps Formik registered on the address field while the Google widget owns the visible UI. */}
      <input type="hidden" {...field} value={field.value ?? ''} />

      <div data-testid="address-autocomplete-host">
        {useFallbackInput ? (
          <Form.Control>
            <Form.Input
              name={field.name}
              value={field.value ?? ''}
              onChange={handleFallbackChange}
              onBlur={field.onBlur}
              type="text"
              autoComplete="off"
              data-testid="address-autocomplete"
            />
          </Form.Control>
        ) : (
          <div ref={hostRef} />
        )}

        {isLoadingPlace && (
          <p className="help" style={{ marginTop: '0.25rem' }}>
            …
          </p>
        )}
      </div>
    </FormField>
  )
}

export default AddressAutocompleteField

type ParsedManualAddress = {
  address: string
  streetAddress: string
  zip: string
  city: string
  isComplete: boolean
}

/**
 * Parse `street, zip city` typed manually when Google autocomplete is unavailable.
 */
function parseManualFallbackAddress(raw: string): ParsedManualAddress {
  const trimmed = raw.trim()
  const match = trimmed.match(/^(.+?),\s*(\d{4,6})\s+(.+)$/)
  if (match) {
    const streetLine = match[1].trim()
    return {
      address: trimmed,
      streetAddress: streetLine,
      zip: match[2],
      city: match[3].trim(),
      isComplete: /^\d+\s/.test(streetLine),
    }
  }

  return {
    address: trimmed,
    streetAddress: trimmed.split(',')[0]?.trim() ?? trimmed,
    zip: '',
    city: '',
    isComplete: false,
  }
}

/**
 * Build the same label shown in the Google autocomplete dropdown (main + secondary line).
 */
function getPlacePredictionDisplayText(
  prediction: google.maps.places.PlacePrediction,
): string | undefined {
  const mainText = prediction.mainText?.text?.trim() || prediction.text?.text?.trim()
  const secondaryText = prediction.secondaryText?.text?.trim()

  if (mainText && secondaryText) {
    return `${mainText}, ${secondaryText}`
  }

  return mainText
}
